import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import "./ContentSection1.css";

const DEFAULT_AUTOPLAY_MS = 5000;
const DEFAULT_IMAGES = [{ src: "/biba_graphics/2.jpg", durationMs: DEFAULT_AUTOPLAY_MS }];

const ContentSection1 = ({ location = "Home Banner", defaultImages = DEFAULT_IMAGES }) => {
  const [images, setImages] = useState(defaultImages);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // On routes prerendered at build time (e.g. Home), the slide div's DOM
  // node is left over from that static snapshot; if it happened to already
  // show the same file as DEFAULT_IMAGES, React's update after the async
  // Firestore fetch below can leave that node's background-image stale even
  // though `images` state is correct (confirmed via React DevTools: state
  // updates, DOM attribute doesn't). Flipping this key once real data
  // arrives forces React to discard that node and mount a fresh one.
  const [loadedFromFirestore, setLoadedFromFirestore] = useState(false);
  const autoPlayRef = useRef(null);
  const touchStartX = useRef(null);

  // Only the visible slide's image is requested at first. Every slide used to set its
  // background-image straight away, so the 2nd slide (a multi-hundred-KB banner nobody
  // sees for 5 s) downloaded in parallel with the 1st and slowed the first paint on a
  // phone connection. The rest are fetched quietly once the page has finished loading,
  // and a slide that is reached before its image arrived simply loads on demand.
  const [readySlides, setReadySlides] = useState(() => new Set([0]));
  useEffect(() => {
    let cancelled = false;
    let timer;
    setReadySlides(new Set([0]));
    const fetchRest = () => {
      images.forEach((img, i) => {
        if (i === 0 || !img.src) return;
        const probe = new Image();
        probe.onload = probe.onerror = () => {
          if (!cancelled) setReadySlides((prev) => new Set(prev).add(i));
        };
        probe.src = img.src;
      });
    };
    const afterLoad = () => {
      timer = setTimeout(fetchRest, 1500);
    };
    if (document.readyState === "complete") afterLoad();
    else window.addEventListener("load", afterLoad, { once: true });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener("load", afterLoad);
    };
  }, [images]);

  // Fetches home_banner docs whose location matches the `location` prop and
  // status === true, then swaps them in for the default local slide image.
  // Each banner carries its own on-screen duration (durationSeconds in
  // Firestore, set from Tixaru's Biba Website > Banners page), defaulting
  // to DEFAULT_AUTOPLAY_MS for banners saved before that field existed.
  useEffect(() => {
    const loadBanners = async () => {
      try {
        const bannerQuery = query(
          collection(db, "home_banner"),
          where("location", "==", location),
          where("status", "==", true)
        );
        const snapshot = await getDocs(bannerQuery);

        const banners = snapshot.docs
          .map((doc) => doc.data())
          .sort((a, b) => {
            const orderDiff = (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
            return orderDiff !== 0 ? orderDiff : (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
          })
          .map((banner) => ({
            src: banner.image,
            durationMs: (Number(banner.durationSeconds) || DEFAULT_AUTOPLAY_MS / 1000) * 1000,
          }))
          .filter((banner) => Boolean(banner.src));

        if (banners.length > 0) {
          setImages(banners);
          setCurrentIndex(0);
          setPrevIndex(null);
          setLoadedFromFirestore(true);
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadBanners();
  }, [location]);

  /* ─── navigate ─────────────────────────────────────────── */
  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setPrevIndex(currentIndex);
    setCurrentIndex(index);
    setTimeout(() => { setIsTransitioning(false); setPrevIndex(null); }, 700);
  }, [currentIndex, isTransitioning]);

  const goToNext = useCallback(() => goToSlide((currentIndex + 1) % images.length), [currentIndex, goToSlide, images.length]);
  const goToPrevious = useCallback(() => goToSlide((currentIndex - 1 + images.length) % images.length), [currentIndex, goToSlide, images.length]);

  /* ─── auto-play ─────────────────────────────────────────── */
  // Each slide advances after its own duration rather than a single fixed
  // interval, so re-arm with a timeout (not setInterval) whenever the
  // current slide or its images change.
  const resetAutoPlay = useCallback(() => {
    clearTimeout(autoPlayRef.current);
    const duration = images[currentIndex]?.durationMs ?? DEFAULT_AUTOPLAY_MS;
    autoPlayRef.current = setTimeout(goToNext, duration);
  }, [goToNext, images, currentIndex]);

  useEffect(() => {
    resetAutoPlay();
    return () => clearTimeout(autoPlayRef.current);
  }, [resetAutoPlay]);

  /* ─── keyboard ──────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") { goToPrevious(); resetAutoPlay(); }
      if (e.key === "ArrowRight") { goToNext(); resetAutoPlay(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goToNext, goToPrevious, resetAutoPlay]);

  /* ─── swipe ─────────────────────────────────────────────── */
  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) { delta < 0 ? goToNext() : goToPrevious(); resetAutoPlay(); }
    touchStartX.current = null;
  };

  /* ─── slide class helper ─────────────────────────────────── */
  const slideClass = (i) => {
    let c = "slide";
    if (i === currentIndex) c += " active";
    else if (i === prevIndex) c += " prev";
    return c;
  };

  if (images.length === 0) return null;

  return (
    <div
      key={loadedFromFirestore ? "live" : "default"}
      className="slider-container"
      onMouseEnter={() => clearTimeout(autoPlayRef.current)}
      onMouseLeave={resetAutoPlay}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-label="Image Slider"
    >

      {/* ── SLIDES: each slide is a div with background-image ──
          This is the KEY fix:
          - background-size: contain  → never crops the image
          - background-position: center center → centres it
          No <img> with object-fit:cover that clips content     */}
      {images.map((img, i) => (
        <div
          key={i}
          className={slideClass(i)}
          style={i === currentIndex || readySlides.has(i) ? { backgroundImage: `url(${img.src})` } : undefined}
          role="img"
          aria-label={`Slide ${i + 1}`}
          aria-hidden={i !== currentIndex}
        />
      ))}

      {/* ── DOTS: absolute inside .slider-container ── */}
      <div className="slider-dots" role="tablist" aria-label="Slides">
        {images.map((_, i) => (
          <span
            key={i}
            className={`slider-dot${i === currentIndex ? " active" : ""}`}
            onClick={() => { goToSlide(i); resetAutoPlay(); }}
            role="tab"
            aria-selected={i === currentIndex}
            aria-label={`Go to slide ${i + 1}`}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goToSlide(i)}
          />
        ))}
      </div>

      {/* ── ARROWS ── */}
      <button
        className="slider-arrow left"
        onClick={() => { goToPrevious(); resetAutoPlay(); }}
        aria-label="Previous slide"
        disabled={isTransitioning}
      >
        &#10094;
      </button>

      <button
        className="slider-arrow right"
        onClick={() => { goToNext(); resetAutoPlay(); }}
        aria-label="Next slide"
        disabled={isTransitioning}
      >
        &#10095;
      </button>

    </div>
  );
};

export default ContentSection1;
